import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Instagram, Twitter } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = 'https://filmcable.net';

export default function WriterProfile() {
  const { slug } = useParams();
  const [writer, setWriter] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWriter();
  }, [slug]);

  const fetchWriter = async () => {
    try {
      const [writerRes, articlesRes] = await Promise.all([
        axios.get(`${API}/writers/${slug}`),
        axios.get(`${API}/writers/${slug}/articles`)
      ]);
      setWriter(writerRes.data);
      setArticles(articlesRes.data);
    } catch (error) {
      console.error("Error fetching writer:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen" data-testid="writer-loading">
        <div className="container py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 items-start mb-12">
              <div className="w-32 h-32 skeleton rounded-full" />
              <div className="flex-1 space-y-4">
                <div className="h-8 w-48 skeleton" />
                <div className="h-4 w-full skeleton" />
                <div className="h-4 w-3/4 skeleton" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!writer) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="writer-not-found">
        <div className="text-center">
          <h1 className="font-serif text-4xl font-bold mb-4">Writer Not Found</h1>
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-[#FF66C4] hover:underline"
          >
            <ArrowLeft size={18} />
            Back to Articles
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="writer-profile-page">
      <SEO
        title={writer.name}
        description={writer.bio || `Articles by ${writer.name} on Filmcable`}
        image={writer.profile_image}
        url={`${SITE_URL}/writers/${writer.slug}`}
      />

      {/* Back Navigation */}
      <div className="container py-6">
        <Link
          to="/articles"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={16} />
          <span className="text-sm uppercase tracking-wider">Back to Editorial</span>
        </Link>
      </div>

      {/* Writer Header */}
      <section className="border-t border-zinc-100 py-12 md:py-16">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 items-center md:items-start text-center md:text-left">
              {/* Profile Image */}
              {writer.profile_image ? (
                <img
                  src={writer.profile_image}
                  alt={writer.name}
                  className="w-32 h-32 md:w-40 md:h-40 object-cover rounded-full"
                />
              ) : (
                <div className="w-32 h-32 md:w-40 md:h-40 bg-zinc-200 rounded-full flex items-center justify-center">
                  <span className="font-serif text-4xl text-zinc-400">
                    {writer.name.charAt(0)}
                  </span>
                </div>
              )}

              {/* Writer Info */}
              <div className="flex-1">
                <h1 className="font-serif text-3xl md:text-4xl font-bold mb-4">
                  {writer.name}
                </h1>
                {writer.bio && (
                  <p className="text-lg text-zinc-600 leading-relaxed mb-6">
                    {writer.bio}
                  </p>
                )}
                
                {/* Social Links */}
                {(writer.instagram || writer.twitter) && (
                  <div className="flex items-center justify-center md:justify-start gap-4">
                    {writer.instagram && (
                      <a
                        href={`https://instagram.com/${writer.instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-zinc-500 hover:text-[#FF66C4] transition-colors"
                      >
                        <Instagram size={18} />
                        <span className="text-sm">@{writer.instagram}</span>
                      </a>
                    )}
                    {writer.twitter && (
                      <a
                        href={`https://twitter.com/${writer.twitter}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-zinc-500 hover:text-[#FF66C4] transition-colors"
                      >
                        <Twitter size={18} />
                        <span className="text-sm">@{writer.twitter}</span>
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Writer's Articles */}
      <section className="py-12 md:py-16 bg-zinc-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-serif text-2xl font-bold mb-2">
              Articles by {writer.name}
            </h2>
            <div className="accent-line mb-8" />

            {articles.length > 0 ? (
              <div className="space-y-0 divide-y divide-zinc-200 bg-white">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/articles/${article.slug}`}
                    className="flex gap-4 md:gap-6 p-4 md:p-6 group hover:bg-zinc-50 transition-colors"
                    data-testid={`writer-article-${article.slug}`}
                  >
                    {/* Thumbnail */}
                    <div className="w-24 h-24 md:w-32 md:h-32 flex-shrink-0 overflow-hidden bg-zinc-100">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                      <span className="font-accent text-xs font-bold uppercase tracking-[0.15em] text-[#FF66C4] mb-2">
                        {article.category}
                      </span>
                      <h3 className="font-serif text-lg md:text-xl font-bold leading-tight mb-2 group-hover:text-[#FF66C4] transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-zinc-500 line-clamp-2 hidden md:block mb-2">
                        {article.excerpt}
                      </p>
                      <p className="text-sm text-zinc-400">
                        {formatDate(article.created_at)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white">
                <p className="text-zinc-500">No articles published yet.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
